// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * Folding a BOM-shaped table by level (design doc §10.6, §7.3).
 *
 * The BOM page and the archive page both show a tree as an indented table — hundreds of
 * rows of it — and both fold it the same way: a node can be folded to hide everything
 * under it, and the whole table can be opened to a level ("show two levels"). The rules
 * live here once so the two pages cannot disagree about what a fold hides.
 *
 * Nodes are rows: `key` the row, `parentKey` the row it sits under (null at the top),
 * `depth` its level, 0 for the top. A parent need not come before its children in the
 * array.
 */

export interface FoldNode {
  key: string;
  parentKey: string | null;
  depth: number;
}

/** The rows that have something under them, and so can be folded. */
export function parentKeys(nodes: FoldNode[]): Set<string> {
  const keys = new Set(nodes.map((node) => node.key));
  return new Set(
    nodes
      .map((node) => node.parentKey)
      .filter((key): key is string => key !== null && keys.has(key)),
  );
}

/** The rows hidden because some row above them is folded. */
export function hiddenKeys(nodes: FoldNode[], folded: ReadonlySet<string>): Set<string> {
  const parentOf = new Map(nodes.map((node) => [node.key, node.parentKey]));
  const hidden = new Set<string>();
  for (const node of nodes) {
    // Walk up; `seen` guards against a cycle in data that should never have one.
    const seen = new Set<string>();
    let parent = node.parentKey;
    while (parent !== null && parent !== undefined && !seen.has(parent)) {
      if (folded.has(parent)) {
        hidden.add(node.key);
        break;
      }
      seen.add(parent);
      parent = parentOf.get(parent) ?? null;
    }
  }
  return hidden;
}

/** The deepest level present, counted from 1 — the number of levels the table has. */
export function levelCount(nodes: FoldNode[]): number {
  return nodes.reduce((deepest, node) => Math.max(deepest, node.depth + 1), 0);
}

/**
 * The folds that show exactly `level` levels: every row with children at the last shown
 * level or below is folded. Folding the deeper ones too means that opening one node shows
 * its next level, not its whole subtree at once.
 */
export function foldedToLevel(nodes: FoldNode[], level: number): Set<string> {
  const parents = parentKeys(nodes);
  return new Set(
    nodes
      .filter((node) => parents.has(node.key) && node.depth >= level - 1)
      .map((node) => node.key),
  );
}
